import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { Browser, SearchHit } from '@vault/shared';
import { Button, EmptyState, Favicon, Icon, Skeleton } from '@vault/ui';
import { ApiError, api } from '../api/client.js';
import type { SearchParams } from '../api/client.js';
import { useT } from '../i18n/index.js';
import type { MessageKey } from '../i18n/index.js';
import {
  browserDotColor,
  browserLabel,
  faviconIndex,
  faviconLetter,
  fmtDate,
  fmtTime,
  highlightTerms,
  hostname,
  matchedReason,
  renderSnippet,
} from './search/searchHelpers.js';

type Mode = NonNullable<SearchParams['mode']>;
type SortKey = 'relevance' | 'date';
type RangeKey = 'any' | '24h' | '7d' | '30d' | '1y';

const MODES: Array<{ key: Mode; labelKey: MessageKey }> = [
  { key: 'text', labelKey: 'search.mode.text' },
  { key: 'hybrid', labelKey: 'search.mode.hybrid' },
  { key: 'semantic', labelKey: 'search.mode.semantic' },
];

const RANGES: Array<{ key: RangeKey; labelKey: MessageKey }> = [
  { key: 'any', labelKey: 'search.range.any' },
  { key: '24h', labelKey: 'search.range.24h' },
  { key: '7d', labelKey: 'search.range.7d' },
  { key: '30d', labelKey: 'search.range.30d' },
  { key: '1y', labelKey: 'search.range.1y' },
];

const RANGE_MS: Record<Exclude<RangeKey, 'any'>, number> = {
  '24h': 86_400_000,
  '7d': 7 * 86_400_000,
  '30d': 30 * 86_400_000,
  '1y': 365 * 86_400_000,
};

const BROWSER_FACETS: Array<{ key: Browser; label: string }> = [
  { key: 'chrome', label: 'Chrome' },
  { key: 'yandex', label: 'Yandex' },
  { key: 'edge', label: 'Edge' },
  { key: 'comet', label: 'Comet' },
];

const PAGE_SIZE = 30;
const SUGGESTIONS = ['mortgage rates', 'react hooks', 'flight booking'];

export function SearchPage() {
  const navigate = useNavigate();
  const { t } = useT();

  // Seed from the ?q= deep link (extension "Search vault for this site" and the
  // popup's "open full search" both pass ?q=…). Without this the query is
  // silently dropped and the user lands on an empty search.
  const [searchParams] = useSearchParams();
  const initialQuery = (searchParams.get('q') ?? '').trim();

  const [input, setInput] = useState(initialQuery);
  const [q, setQ] = useState(initialQuery);
  const [mode, setMode] = useState<Mode>('hybrid');
  const [sort, setSort] = useState<SortKey>('relevance');
  const [browser, setBrowser] = useState<string | null>(null);
  const [range, setRange] = useState<RangeKey>('any');
  const [limit, setLimit] = useState(PAGE_SIZE);

  // debounce the raw field into the committed query used for fetching
  useEffect(() => {
    const id = window.setTimeout(() => setQ(input.trim()), 220);
    return () => window.clearTimeout(id);
  }, [input]);

  // reset paging whenever the query shape changes
  useEffect(() => setLimit(PAGE_SIZE), [q, mode, browser, range]);

  const from = useMemo(() => (range === 'any' ? undefined : Date.now() - RANGE_MS[range]), [range]);

  const usage = useQuery({ queryKey: ['usage'], queryFn: () => api.account.usage() });
  const plan = usage.data?.plan;
  const isFree = plan === 'free';

  const semanticBlocked = mode === 'semantic' && isFree;
  const enabled = q.length > 0 && !semanticBlocked;

  const results = useQuery({
    queryKey: ['search', q, mode, browser ?? '', from ?? 0, limit],
    queryFn: () =>
      api.search(q, {
        mode,
        limit,
        browser: browser ?? undefined,
        from,
      }),
    enabled,
    placeholderData: keepPreviousData,
  });

  const hits = results.data?.hits ?? [];
  const err = results.error;
  const upsell = semanticBlocked || (mode === 'semantic' && err instanceof ApiError && err.status === 402);

  // ── derived facets (from the current result set) ──────────────────────────
  const browserCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const h of hits) if (h.browser) m.set(h.browser, (m.get(h.browser) ?? 0) + 1);
    return m;
  }, [hits]);

  const topDomains = useMemo(() => {
    const m = new Map<string, number>();
    for (const h of hits) {
      const d = hostname(h.url);
      m.set(d, (m.get(d) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [hits]);

  const histogram = useMemo(() => buildHistogram(hits), [hits]);

  // ── ranked / sorted views ─────────────────────────────────────────────────
  const bestHit = useMemo(() => {
    if (hits.length === 0) return null;
    return [...hits].sort((a, b) => b.score - a.score)[0] ?? null;
  }, [hits]);

  const listHits = useMemo(() => {
    const rest = bestHit ? hits.filter((h) => h.itemId !== bestHit.itemId) : hits;
    const sorted = [...rest];
    if (sort === 'date') sorted.sort((a, b) => b.savedAt - a.savedAt);
    else sorted.sort((a, b) => b.score - a.score);
    return sorted;
  }, [hits, bestHit, sort]);

  const openUrl = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

  const runSuggestion = (s: string) => {
    setInput(s);
    setQ(s);
  };

  const loading = enabled && !upsell && results.isLoading;
  const hardError = enabled && !upsell && results.isError;
  const showResults = enabled && !upsell && !hardError;

  return (
    <div className="tv-search">
      <style>{CSS}</style>

      {/* ── toolbar ── */}
      <div className="tv-search__bar">
        <label className="tv-field" aria-label={t('shell.searchPlaceholder')}>
          <Icon name="search" size={14} color="var(--text-2)" />
          <input
            className="tv-field__input"
            placeholder={t('search.placeholder')}
            value={input}
            autoFocus
            onChange={(e: ChangeEvent<HTMLInputElement>) => setInput(e.target.value)}
          />
          {input && (
            <button className="tv-field__clear" aria-label={t('search.clear')} onClick={() => setInput('')}>
              <Icon name="x" size={9} color="var(--text-2)" />
            </button>
          )}
        </label>

        <span className="tv-seg-group" role="tablist" aria-label="Search mode">
          {MODES.map((m) => (
            <button
              key={m.key}
              className="tv-seg"
              role="tab"
              aria-selected={mode === m.key}
              data-active={mode === m.key}
              onClick={() => setMode(m.key)}
            >
              {t(m.labelKey)}
              {m.key === 'semantic' && isFree && (
                <Icon name="lock" size={10} color="var(--text-3)" style={{ marginLeft: 5 }} />
              )}
            </button>
          ))}
        </span>

        <span className="tv-status">
          <span className="tv-status__dot" />
          {usage.data
            ? t('search.status.counted', { count: usage.data.counts.items.toLocaleString() })
            : t('search.status.default')}
        </span>
      </div>

      {/* ── body ── */}
      <div className="tv-search__body">
        <div className="tv-search__main">
          {!enabled && !upsell && (
            <div style={{ paddingTop: 40 }}>
              <EmptyState
                dashed={false}
                icon={<Icon name="search" size={18} color="var(--text-3)" />}
                title={t('search.empty.title')}
                description={t('search.empty.body')}
                action={
                  <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginTop: 4 }}>
                    {SUGGESTIONS.map((s) => (
                      <button key={s} className="tv-chip" onClick={() => runSuggestion(s)}>
                        {s}
                      </button>
                    ))}
                  </span>
                }
              />
            </div>
          )}

          {upsell && <SemanticUpsell onSeePlans={() => navigate('/pricing')} onUseText={() => setMode('text')} />}

          {loading && <ResultsSkeleton />}

          {hardError && (
            <div className="tv-card tv-card--pad" style={{ gap: 10, alignItems: 'flex-start' }}>
              <span className="tv-eyebrow" style={{ color: 'var(--danger)' }}>{t('search.failed.eyebrow')}</span>
              <span style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>
                {err instanceof ApiError ? err.message : t('search.failed.body')} {t('search.failed.retryHint')}
              </span>
              <Button variant="ghost" size="sm" onClick={() => void results.refetch()}>
                {t('common.tryAgain')}
              </Button>
            </div>
          )}

          {showResults && !loading && !hardError && hits.length === 0 && (
            <div style={{ paddingTop: 40 }}>
              <EmptyState
                icon={<Icon name="search" size={18} color="var(--text-3)" />}
                title={t('search.noMatch.title', { q })}
                description={t('search.noMatch.body')}
                action={
                  (browser || range !== 'any') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setBrowser(null);
                        setRange('any');
                      }}
                    >
                      {t('common.clearFilters')}
                    </Button>
                  )
                }
              />
            </div>
          )}

          {showResults && !loading && !hardError && hits.length > 0 && (
            <>
              {bestHit && <BestMatch hit={bestHit} query={q} onOpen={openUrl} />}

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="tv-eyebrow">— {t('search.results', { count: hits.length.toLocaleString() })}</span>
                <span className="tv-seg-group" style={{ marginInlineStart: 'auto' }} role="tablist" aria-label="Sort">
                  <button className="tv-seg" data-active={sort === 'relevance'} onClick={() => setSort('relevance')}>
                    {t('search.sort.relevance')}
                  </button>
                  <button className="tv-seg" data-active={sort === 'date'} onClick={() => setSort('date')}>
                    {t('search.sort.date')}
                  </button>
                </span>
              </div>

              {listHits.length > 0 && (
                <div className="tv-card" style={{ overflow: 'hidden' }}>
                  {listHits.map((h) => (
                    <ResultRow key={h.itemId} hit={h} query={q} onOpen={openUrl} />
                  ))}
                </div>
              )}

              {hits.length >= limit && (
                <button
                  className="tv-more"
                  onClick={() => setLimit((l) => l + PAGE_SIZE)}
                  disabled={results.isFetching}
                >
                  {results.isFetching ? t('common.loading') : t('search.loadMore')}
                </button>
              )}
            </>
          )}
        </div>

        {/* ── refine rail ── */}
        <aside className="tv-search__rail">
          <div className="tv-card tv-card--pad" style={{ gap: 11 }}>
            <span className="tv-eyebrow tv-eyebrow--wide">{t('search.facet.browser')}</span>
            {BROWSER_FACETS.map((b) => {
              const active = browser === b.key;
              return (
                <button
                  key={b.key}
                  className="tv-facet"
                  data-active={active}
                  onClick={() => setBrowser(active ? null : b.key)}
                >
                  <span className="tv-facet__box" data-active={active}>
                    {active && <Icon name="check" size={9} color="#fff" strokeWidth={2.4} />}
                  </span>
                  <span className="tv-facet__dot" style={{ background: browserDotColor(b.key) }} />
                  {b.label}
                  <span className="tv-facet__count">{browserCounts.get(b.key) ?? 0}</span>
                </button>
              );
            })}
          </div>

          <div className="tv-card tv-card--pad" style={{ gap: 10 }}>
            <span className="tv-eyebrow tv-eyebrow--wide">{t('search.facet.date')}</span>
            {histogram && (
              <div className="tv-hist" aria-hidden="true">
                {histogram.bars.map((h, i) => (
                  <span key={i} className="tv-hist__bar" style={{ height: `${Math.max(8, h * 100)}%` }} />
                ))}
              </div>
            )}
            <span className="tv-range">
              {RANGES.map((r) => (
                <button key={r.key} className="tv-range__opt" data-active={range === r.key} onClick={() => setRange(r.key)}>
                  {t(r.labelKey)}
                </button>
              ))}
            </span>
          </div>

          {topDomains.length > 0 && (
            <div className="tv-card tv-card--pad" style={{ gap: 9 }}>
              <span className="tv-eyebrow tv-eyebrow--wide">{t('search.facet.topDomains')}</span>
              {topDomains.map(([d, n]) => (
                <span key={d} className="tv-domain">
                  {d}
                  <span className="tv-domain__count">{n}</span>
                </span>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

// ── best-match banner ─────────────────────────────────────────────────────────
function BestMatch({ hit, query, onOpen }: { hit: SearchHit; query: string; onOpen: (url: string) => void }) {
  const { t } = useT();
  const domain = hostname(hit.url);
  const b = browserLabel(hit.browser);
  return (
    <div className="tv-best" onClick={() => onOpen(hit.url)}>
      <span className="tv-eyebrow" style={{ color: 'var(--accent-ink)', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
        <Icon name="sparkle" size={11} color="currentColor" strokeWidth={1.5} />
        {t('search.bestMatch')}{b ? ` · ${t('search.openedIn', { browser: b })}` : ''} · {fmtDate(hit.savedAt).toUpperCase()} · {fmtTime(hit.savedAt)}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Favicon letter={faviconLetter(domain)} index={faviconIndex(domain)} size={22} ring="var(--surface-1)" />
        <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: 1 }}>
          <span style={{ fontSize: 14.5, fontWeight: 600 }}>{highlightTerms(hit.title, query)}</span>
          {hit.snippet && (
            <span style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--text-2)' }}>{renderSnippet(hit.snippet, query)}</span>
          )}
          <span className="tv-meta">
            <span>{domain.toUpperCase()}</span>
            <span>·</span>
            <span>{fmtDate(hit.savedAt)}</span>
            {b && (
              <>
                <span>·</span>
                <span className="tv-meta__dot" style={{ background: browserDotColor(hit.browser) }} />
                <span>{b}</span>
              </>
            )}
          </span>
        </span>
        <span onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
          <Button variant="primary" size="sm" icon={<Icon name="restore" size={11} color="currentColor" />} onClick={() => onOpen(hit.url)}>
            {t('common.restore')}
          </Button>
        </span>
      </div>
    </div>
  );
}

// ── result row ────────────────────────────────────────────────────────────────
function ResultRow({ hit, query, onOpen }: { hit: SearchHit; query: string; onOpen: (url: string) => void }) {
  const { t } = useT();
  const domain = hostname(hit.url);
  const b = browserLabel(hit.browser);
  const reason = matchedReason(hit.matchedOn);
  return (
    <div className="tv-srow" onClick={() => onOpen(hit.url)}>
      <Favicon letter={faviconLetter(domain)} index={faviconIndex(domain)} size={20} ring="var(--surface-1)" />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1 }}>
        <span style={{ fontSize: 13.5, fontWeight: 600 }}>{highlightTerms(hit.title, query)}</span>
        {hit.snippet && (
          <span style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--text-2)' }}>{renderSnippet(hit.snippet, query)}</span>
        )}
        <span className="tv-meta">
          <span>{domain.toUpperCase()}</span>
          <span>·</span>
          <span>{fmtDate(hit.savedAt)}</span>
          {b && (
            <>
              <span>·</span>
              <span className="tv-meta__dot" style={{ background: browserDotColor(hit.browser) }} />
              <span>{b}</span>
            </>
          )}
          {reason && (
            <>
              <span>·</span>
              <span style={{ color: reason.strong ? 'var(--primary)' : 'var(--text-3)' }}>{reason.label.toUpperCase()}</span>
            </>
          )}
        </span>
      </span>
      <span onClick={(e) => e.stopPropagation()} style={{ alignSelf: 'center', flexShrink: 0 }}>
        <Button variant="secondary" size="sm" onClick={() => onOpen(hit.url)}>
          {t('common.restore')}
        </Button>
      </span>
    </div>
  );
}

// ── semantic upsell ───────────────────────────────────────────────────────────
function SemanticUpsell({ onSeePlans, onUseText }: { onSeePlans: () => void; onUseText: () => void }) {
  const { t } = useT();
  return (
    <div className="tv-best" style={{ cursor: 'default' }}>
      <span className="tv-eyebrow" style={{ color: 'var(--accent-ink)', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
        <Icon name="sparkle" size={11} color="currentColor" strokeWidth={1.5} />
        {t('search.upsell.eyebrow')}
      </span>
      <span style={{ fontSize: 15, fontWeight: 600 }}>{t('search.upsell.title')}</span>
      <span style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-2)', maxWidth: 460 }}>
        {t('search.upsell.body')}
      </span>
      <span style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <Button variant="primary" size="sm" icon={<Icon name="sparkle" size={11} color="currentColor" strokeWidth={1.5} />} onClick={onSeePlans}>
          {t('search.upsell.seePlans')}
        </Button>
        <Button variant="ghost" size="sm" onClick={onUseText}>
          {t('search.upsell.useText')}
        </Button>
      </span>
    </div>
  );
}

// ── loading skeleton ──────────────────────────────────────────────────────────
function ResultsSkeleton() {
  return (
    <>
      <div className="tv-best" style={{ cursor: 'default', gap: 12 }}>
        <Skeleton width={220} height={10} />
        <Skeleton width="70%" height={16} />
        <Skeleton width="90%" height={12} />
      </div>
      <Skeleton width={120} height={10} />
      <div className="tv-card" style={{ overflow: 'hidden' }}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="tv-srow" style={{ cursor: 'default' }}>
            <Skeleton width={20} height={20} radius={5} />
            <span style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
              <Skeleton width="55%" height={13} />
              <Skeleton width="85%" height={11} />
              <Skeleton width={180} height={9} />
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

// ── date histogram from the current result set ────────────────────────────────
function buildHistogram(hits: SearchHit[]): { bars: number[] } | null {
  if (hits.length === 0) return null;
  const times = hits.map((h) => h.savedAt);
  const min = Math.min(...times);
  const max = Math.max(...times);
  const buckets = 6;
  const span = Math.max(1, max - min);
  const counts = new Array<number>(buckets).fill(0);
  for (const t of times) {
    const idx = Math.min(buckets - 1, Math.floor(((t - min) / span) * buckets));
    counts[idx] = (counts[idx] ?? 0) + 1;
  }
  const peak = Math.max(...counts, 1);
  return { bars: counts.map((c) => c / peak) };
}

// ── scoped styles ─────────────────────────────────────────────────────────────
const CSS = `
.tv-search{display:flex;flex-direction:column;min-height:100%;font-family:var(--font-body);color:var(--text-1)}
.tv-search__bar{display:flex;align-items:center;gap:12px;min-height:56px;padding:12px 28px;border-bottom:1px solid var(--line);flex-wrap:wrap}
.tv-search__body{display:grid;grid-template-columns:minmax(0,1fr) 264px;gap:24px;padding:24px 28px;align-items:start}
.tv-search__main{min-width:0;display:flex;flex-direction:column;gap:12px}
.tv-search__rail{display:flex;flex-direction:column;gap:14px;min-width:0}
@media (max-width:1080px){.tv-search__body{grid-template-columns:minmax(0,1fr)}}
@media (max-width:640px){.tv-search__bar{padding:12px 16px}.tv-search__body{padding:16px}}

.tv-field{display:flex;align-items:center;gap:9px;background:var(--surface-1);border:1px solid var(--line-primary);border-radius:var(--r-md);padding:0 12px;height:36px;flex:1 1 320px;min-width:220px;max-width:520px;box-shadow:0 0 0 3px var(--primary-tint)}
.tv-field__input{flex:1;min-width:0;background:transparent;border:none;outline:none;color:var(--text-1);font-size:13px;font-family:inherit}
.tv-field__clear{display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;background:var(--surface-3);border:none;cursor:pointer;flex-shrink:0}

.tv-seg-group{display:inline-flex;border:1px solid var(--line-strong);border-radius:999px;padding:2px;gap:2px}
.tv-seg{display:inline-flex;align-items:center;border-radius:999px;padding:5px 12px;font-size:11.5px;font-weight:500;color:var(--text-2);cursor:pointer;background:transparent;border:none;font-family:var(--font-body);white-space:nowrap}
.tv-seg[data-active="true"]{background:var(--surface-3);color:var(--text-1);font-weight:600}

.tv-status{display:inline-flex;align-items:center;gap:6px;margin-left:auto;border:1px solid var(--line);border-radius:999px;padding:5px 11px;font-family:var(--font-mono);font-size:9.5px;letter-spacing:.04em;color:var(--text-2);white-space:nowrap}
.tv-status__dot{width:6px;height:6px;border-radius:50%;background:var(--success)}

.tv-eyebrow{font-family:var(--font-mono);font-size:10px;letter-spacing:.12em;color:var(--text-3)}
.tv-eyebrow--wide{font-size:9.5px;letter-spacing:.14em}

.tv-card{display:flex;flex-direction:column;background:var(--surface-1);border:1px solid var(--line);border-radius:var(--r-lg)}
.tv-card--pad{padding:16px}

.tv-best{background:linear-gradient(135deg, var(--accent-tint), transparent 60%), var(--surface-1);border:1px solid var(--line-accent);border-radius:var(--r-lg);padding:16px;display:flex;flex-direction:column;gap:10px;cursor:pointer}

.tv-srow{display:flex;gap:12px;padding:14px 16px;border-bottom:1px solid var(--line);cursor:pointer;background:transparent;transition:background var(--t-fast) var(--ease)}
.tv-srow:last-child{border-bottom:none}
.tv-srow:hover{background:var(--surface-2)}

.tv-meta{display:inline-flex;align-items:center;gap:6px;flex-wrap:wrap;font-family:var(--font-mono);font-size:9.5px;letter-spacing:.04em;color:var(--text-3)}
.tv-meta__dot{width:5px;height:5px;border-radius:50%;display:inline-block}

.tv-more{align-self:center;font-size:12px;font-weight:600;color:var(--text-2);border:1px solid var(--line-strong);border-radius:999px;padding:8px 18px;background:transparent;cursor:pointer;font-family:var(--font-body)}
.tv-more:hover{background:var(--surface-1)}
.tv-more:disabled{opacity:.55;cursor:default}

.tv-chip{border:1px solid var(--line-strong);border-radius:999px;padding:6px 12px;font-size:12px;color:var(--text-2);background:transparent;cursor:pointer;font-family:var(--font-body)}
.tv-chip:hover{background:var(--surface-1);color:var(--text-1)}

.tv-facet{display:flex;align-items:center;gap:9px;font-size:12.5px;color:var(--text-1);background:transparent;border:none;cursor:pointer;padding:0;font-family:var(--font-body);text-align:left}
.tv-facet[data-active="false"]{color:var(--text-2)}
.tv-facet__box{width:15px;height:15px;border-radius:5px;border:1.5px solid var(--line-strong);display:inline-flex;align-items:center;justify-content:center;flex-shrink:0}
.tv-facet__box[data-active="true"]{background:var(--primary);border-color:var(--primary)}
.tv-facet__dot{width:6px;height:6px;border-radius:50%;flex-shrink:0}
.tv-facet__count{margin-left:auto;font-family:var(--font-mono);font-size:9.5px;color:var(--text-3)}

.tv-hist{display:flex;align-items:flex-end;gap:4px;height:44px}
.tv-hist__bar{flex:1;border-radius:3px;background:var(--surface-3)}

.tv-range{display:flex;flex-wrap:wrap;gap:5px}
.tv-range__opt{border:1px solid var(--line-strong);border-radius:999px;padding:4px 10px;font-size:10.5px;color:var(--text-2);background:transparent;cursor:pointer;font-family:var(--font-body)}
.tv-range__opt[data-active="true"]{background:var(--primary-tint);border-color:var(--line-primary);color:var(--primary);font-weight:600}

.tv-domain{display:flex;font-family:var(--font-mono);font-size:10.5px;color:var(--text-2)}
.tv-domain__count{margin-left:auto;color:var(--text-3)}
` as const;
